import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import NetworkManager from "../../app/managers/networkManager";
import { config } from "../../app/utils/config";

const initialState = {
  awareness: {
    campaigns: [],
    currentCampaign: null,
    tracking: [],
    teams: [],
    collabs: [],
    collabPending: [],
    collabHistory: [],
    loading: false,
    error: null,
  },
  quizzes: {
    list: [],
    currentQuiz: null,
    tracking: [],
    adminCollabReview: null,
    collabPending: [],
    collabHistory: [],
    currentAttempt: null,
    currentQuestion: null,
    attemptResults: null,
    reviewData: null,
    collabQuizAttempts: [],
    loading: false,
    error: null,
  },
  dashboard: {
    summary: null,
    byTeam: [],
    loading: false,
    error: null,
  },
};

export const vsecuriteSlice = createSlice({
  name: "vsecurite",
  initialState,
  reducers: {
    /** @param {import("@reduxjs/toolkit").Draft<typeof initialState>} state */
    setAwarenessCampaigns(state, action) {
      state.awareness.campaigns = action.payload;
    },
    /** @param {import("@reduxjs/toolkit").Draft<typeof initialState>} state */
    setAwarenessCurrentCampaign(state, action) {
      state.awareness.currentCampaign = action.payload;
    },
    /** @param {import("@reduxjs/toolkit").Draft<typeof initialState>} state */
    setAwarenessTracking(state, action) {
      state.awareness.tracking = action.payload;
    },
    /** @param {import("@reduxjs/toolkit").Draft<typeof initialState>} state */
    setAwarenessCollabPending(state, action) {
      state.awareness.collabPending = action.payload;
    },
    /** @param {import("@reduxjs/toolkit").Draft<typeof initialState>} state */
    setAwarenessCollabHistory(state, action) {
      state.awareness.collabHistory = action.payload;
    },
    /** @param {import("@reduxjs/toolkit").Draft<typeof initialState>} state */
    setAwarenessLoading(state, action) {
      state.awareness.loading = action.payload;
    },
    /** @param {import("@reduxjs/toolkit").Draft<typeof initialState>} state */
    setAwarenessError(state, action) {
      state.awareness.error = action.payload;
    },
    /** @param {import("@reduxjs/toolkit").Draft<typeof initialState>} state */
    setQuizzesList(state, action) {
      state.quizzes.list = action.payload;
    },
    /** @param {import("@reduxjs/toolkit").Draft<typeof initialState>} state */
    setQuizzesCurrentQuiz(state, action) {
      state.quizzes.currentQuiz = action.payload;
    },
    /** @param {import("@reduxjs/toolkit").Draft<typeof initialState>} state */
    setQuizzesTracking(state, action) {
      state.quizzes.tracking = action.payload;
    },
    /** @param {import("@reduxjs/toolkit").Draft<typeof initialState>} state */
    setQuizzesCollabPending(state, action) {
      state.quizzes.collabPending = action.payload;
    },
    /** @param {import("@reduxjs/toolkit").Draft<typeof initialState>} state */
    setQuizzesCollabHistory(state, action) {
      state.quizzes.collabHistory = action.payload;
    },
    /** @param {import("@reduxjs/toolkit").Draft<typeof initialState>} state */
    setQuizzesCurrentAttempt(state, action) {
      state.quizzes.currentAttempt = action.payload;
    },
    /** @param {import("@reduxjs/toolkit").Draft<typeof initialState>} state */
    setQuizzesCurrentQuestion(state, action) {
      state.quizzes.currentQuestion = action.payload;
    },
    /** @param {import("@reduxjs/toolkit").Draft<typeof initialState>} state */
    setQuizzesAttemptResults(state, action) {
      state.quizzes.attemptResults = action.payload;
    },
    /** @param {import("@reduxjs/toolkit").Draft<typeof initialState>} state */
    setQuizzesReviewData(state, action) {
      state.quizzes.reviewData = action.payload;
    },
    /** @param {import("@reduxjs/toolkit").Draft<typeof initialState>} state */
    setQuizzesLoading(state, action) {
      state.quizzes.loading = action.payload;
    },
    /** @param {import("@reduxjs/toolkit").Draft<typeof initialState>} state */
    setQuizzesError(state, action) {
      state.quizzes.error = action.payload;
    },
    /** @param {import("@reduxjs/toolkit").Draft<typeof initialState>} state */
    setDashboardSummary(state, action) {
      state.dashboard.summary = action.payload;
    },
    /** @param {import("@reduxjs/toolkit").Draft<typeof initialState>} state */
    setDashboardByTeam(state, action) {
      state.dashboard.byTeam = action.payload;
    },
    /** @param {import("@reduxjs/toolkit").Draft<typeof initialState>} state */
    setDashboardLoading(state, action) {
      state.dashboard.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    // ── Awareness: loading states ────────────────────────────────────────────
    builder.addCase(fetchAwarenessTeams.fulfilled, (state, action) => {
      if (action.payload.responseData) {
        state.awareness.teams = action.payload.responseData.teams;
      }
    });

    builder.addCase(fetchAwarenessCollabs.fulfilled, (state, action) => {
      if (action.payload.responseData) {
        state.awareness.collabs = action.payload.responseData.collabs;
      }
    });

    builder.addCase(fetchAwarenessCampaigns.pending, (state) => {
      state.awareness.loading = true;
    });
    builder.addCase(fetchAwarenessCampaigns.fulfilled, (state, action) => {
      state.awareness.loading = false;
      if (action.payload.responseData) {
        state.awareness.campaigns = action.payload.responseData.campaigns;
      }
    });
    builder.addCase(fetchAwarenessCampaigns.rejected, (state) => {
      state.awareness.loading = false;
    });

    builder.addCase(fetchAwarenessCampaign.fulfilled, (state, action) => {
      if (action.payload.responseData) {
        state.awareness.currentCampaign = action.payload.responseData.campaign;
      }
    });

    builder.addCase(publishAwarenessCampaign.fulfilled, (state, action) => {
      const id = action.meta.arg.id;
      const idx = state.awareness.campaigns.findIndex((c) => c.id === id);
      if (idx !== -1) {
        state.awareness.campaigns[idx].status = "published";
        state.awareness.campaigns[idx].published_at = new Date().toISOString();
      }
    });

    builder.addCase(deleteAwarenessCampaign.fulfilled, (state, action) => {
      const id = action.meta.arg.id;
      state.awareness.campaigns = state.awareness.campaigns.filter((c) => c.id !== id);
    });

    builder.addCase(fetchAwarenessTracking.fulfilled, (state, action) => {
      if (action.payload.responseData) {
        state.awareness.tracking = action.payload.responseData.tracking;
      }
    });

    builder.addCase(fetchCollabPendingAwareness.pending, (state) => {
      state.awareness.loading = true;
    });
    builder.addCase(fetchCollabPendingAwareness.fulfilled, (state, action) => {
      state.awareness.loading = false;
      if (action.payload.responseData) {
        state.awareness.collabPending = action.payload.responseData.campaigns;
      }
    });
    builder.addCase(fetchCollabPendingAwareness.rejected, (state) => {
      state.awareness.loading = false;
    });

    builder.addCase(fetchCollabAwarenessHistory.pending, (state) => {
      state.awareness.loading = true;
    });
    builder.addCase(fetchCollabAwarenessHistory.fulfilled, (state, action) => {
      state.awareness.loading = false;
      if (action.payload.responseData) {
        state.awareness.collabHistory = action.payload.responseData.campaigns;
      }
    });
    builder.addCase(fetchCollabAwarenessHistory.rejected, (state) => {
      state.awareness.loading = false;
    });

    // ── Quizzes: loading states ──────────────────────────────────────────────
    builder.addCase(fetchQuizzes.pending, (state) => {
      state.quizzes.loading = true;
    });
    builder.addCase(fetchQuizzes.fulfilled, (state, action) => {
      state.quizzes.loading = false;
      if (action.payload.responseData) {
        state.quizzes.list = action.payload.responseData.quizzes;
      }
    });
    builder.addCase(fetchQuizzes.rejected, (state) => {
      state.quizzes.loading = false;
    });

    builder.addCase(fetchQuiz.fulfilled, (state, action) => {
      if (action.payload.responseData) {
        state.quizzes.currentQuiz = action.payload.responseData.quiz;
      }
    });

    builder.addCase(fetchQuizTracking.fulfilled, (state, action) => {
      if (action.payload.responseData) {
        state.quizzes.tracking = action.payload.responseData.tracking;
      }
    });

    builder.addCase(fetchAdminCollabReview.fulfilled, (state, action) => {
      if (action.payload.responseData) {
        state.quizzes.adminCollabReview = action.payload.responseData;
      }
    });

    builder.addCase(fetchCollabPendingQuizzes.pending, (state) => {
      state.quizzes.loading = true;
    });
    builder.addCase(fetchCollabPendingQuizzes.fulfilled, (state, action) => {
      state.quizzes.loading = false;
      if (action.payload.responseData) {
        state.quizzes.collabPending = action.payload.responseData.quizzes;
      }
    });
    builder.addCase(fetchCollabPendingQuizzes.rejected, (state) => {
      state.quizzes.loading = false;
    });

    builder.addCase(fetchCollabQuizHistory.pending, (state) => {
      state.quizzes.loading = true;
    });
    builder.addCase(fetchCollabQuizHistory.fulfilled, (state, action) => {
      state.quizzes.loading = false;
      if (action.payload.responseData) {
        state.quizzes.collabHistory = action.payload.responseData.history || [];
      }
    });
    builder.addCase(fetchCollabQuizHistory.rejected, (state) => {
      state.quizzes.loading = false;
    });

    builder.addCase(fetchQuizReview.fulfilled, (state, action) => {
      if (action.payload.responseData) {
        state.quizzes.reviewData = action.payload.responseData.review;
      }
    });

    builder.addCase(fetchCollabQuizAttempts.fulfilled, (state, action) => {
      if (action.payload.responseData) {
        state.quizzes.collabQuizAttempts = action.payload.responseData.attempts || [];
      }
    });

    builder.addCase(startQuizAttempt.pending, (state) => {
      state.quizzes.loading = true;
    });
    builder.addCase(startQuizAttempt.fulfilled, (state, action) => {
      state.quizzes.loading = false;
      if (action.payload.responseData) {
        state.quizzes.currentAttempt = action.payload.responseData.attempt;
        state.quizzes.currentQuestion = action.payload.responseData.firstQuestion;
      }
    });
    builder.addCase(startQuizAttempt.rejected, (state) => {
      state.quizzes.loading = false;
    });

    builder.addCase(submitAnswer.fulfilled, (state, action) => {
      if (action.payload.responseData) {
        // answerResult contains: is_correct, explanation, and `options` — one entry per
        // option carrying is_correct and was_selected. It has never carried correct_option_ids.
        state.quizzes.currentQuestion = action.payload.responseData.answerResult;
      }
    });

    builder.addCase(finishAttempt.fulfilled, (state, action) => {
      if (action.payload.responseData) {
        state.quizzes.attemptResults = action.payload.responseData.results;
      }
    });

    builder.addCase(fetchAttemptResults.fulfilled, (state, action) => {
      if (action.payload.responseData) {
        state.quizzes.attemptResults = action.payload.responseData.results;
      }
    });

    // ── Dashboard ────────────────────────────────────────────────────────────
    builder.addCase(fetchDashboardSummary.pending, (state) => {
      state.dashboard.loading = true;
    });
    builder.addCase(fetchDashboardSummary.fulfilled, (state, action) => {
      state.dashboard.loading = false;
      if (action.payload.responseData) {
        state.dashboard.summary = action.payload.responseData.summary;
      }
    });
    builder.addCase(fetchDashboardSummary.rejected, (state) => {
      state.dashboard.loading = false;
    });

    builder.addCase(fetchDashboardByTeam.pending, (state) => {
      state.dashboard.loading = true;
    });
    builder.addCase(fetchDashboardByTeam.fulfilled, (state, action) => {
      state.dashboard.loading = false;
      if (action.payload.responseData) {
        state.dashboard.byTeam = action.payload.responseData.byTeam;
      }
    });
    builder.addCase(fetchDashboardByTeam.rejected, (state) => {
      state.dashboard.loading = false;
    });

  },
});

// ── Awareness thunks ─────────────────────────────────────────────────────────

/**
 * Creates a new awareness campaign.
 * @param {object} body - Campaign payload.
 */
export const createAwarenessCampaign = createAsyncThunk(
  "vsecurite/createAwarenessCampaign",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.createAwarenessCampaign,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Fetches the list of all awareness campaigns.
 * @param {object} body - Filter/pagination payload.
 */
export const fetchAwarenessTeams = createAsyncThunk(
  "vsecurite/fetchAwarenessTeams",
  async () => {
    try {
      return await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.fetchAwarenessTeams,
        {}
      );
    } catch (error) {
      return error;
    }
  }
);

/**
 * Fetches the list of active collabs available for individual targeting
 * on awareness campaigns and quizzes.
 */
export const fetchAwarenessCollabs = createAsyncThunk(
  "vsecurite/fetchAwarenessCollabs",
  async () => {
    try {
      return await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.fetchAwarenessCollabs,
        {}
      );
    } catch (error) {
      return error;
    }
  }
);

export const fetchAwarenessCampaigns = createAsyncThunk(
  "vsecurite/fetchAwarenessCampaigns",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.fetchAwarenessCampaigns,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Fetches a single awareness campaign by ID.
 * @param {object} body - Payload containing the campaign ID.
 */
export const fetchAwarenessCampaign = createAsyncThunk(
  "vsecurite/fetchAwarenessCampaign",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.fetchAwarenessCampaign,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Updates an existing awareness campaign.
 * @param {object} body - Updated campaign payload.
 */
export const updateAwarenessCampaign = createAsyncThunk(
  "vsecurite/updateAwarenessCampaign",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.updateAwarenessCampaign,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Deletes an awareness campaign.
 * @param {object} body - Payload containing the campaign ID to delete.
 */
export const deleteAwarenessCampaign = createAsyncThunk(
  "vsecurite/deleteAwarenessCampaign",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.deleteAwarenessCampaign,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Publishes an awareness campaign, making it visible to collaborators.
 * @param {object} body - Payload containing the campaign ID to publish.
 */
export const publishAwarenessCampaign = createAsyncThunk(
  "vsecurite/publishAwarenessCampaign",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.publishAwarenessCampaign,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Resends the "new awareness" email to every assigned collab who hasn't
 * acknowledged the campaign yet. Admin-only.
 * @param {object} body - { id } campaign id
 */
export const resendAwarenessReminder = createAsyncThunk(
  "vsecurite/resendAwarenessReminder",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.resendAwarenessReminder,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Fetches tracking/acknowledgement data for an awareness campaign.
 * @param {object} body - Payload containing the campaign ID.
 */
export const fetchAwarenessTracking = createAsyncThunk(
  "vsecurite/fetchAwarenessTracking",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.fetchAwarenessTracking,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Exports the tracking data for an awareness campaign.
 * @param {object} body - Payload containing export filters.
 */
export const exportAwarenessTracking = createAsyncThunk(
  "vsecurite/exportAwarenessTracking",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.exportAwarenessTracking,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Fetches pending awareness campaigns for a collaborator.
 * @param {object} body - Payload containing the collaborator ID.
 */
export const fetchCollabPendingAwareness = createAsyncThunk(
  "vsecurite/fetchCollabPendingAwareness",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.fetchCollabPendingAwareness,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Fetches the awareness acknowledgement history for a collaborator.
 * @param {object} body - Payload containing the collaborator ID.
 */
export const fetchCollabAwarenessHistory = createAsyncThunk(
  "vsecurite/fetchCollabAwarenessHistory",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.fetchCollabAwarenessHistory,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Records a collaborator's acknowledgement of an awareness campaign.
 * @param {object} body - Payload containing campaign and collaborator IDs.
 */
export const acknowledgeAwareness = createAsyncThunk(
  "vsecurite/acknowledgeAwareness",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.acknowledgeAwareness,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Resets a collaborator's acknowledgement for an awareness campaign.
 * @param {object} body - Payload containing campaign and collaborator IDs.
 */
export const resetAcknowledgement = createAsyncThunk(
  "vsecurite/resetAcknowledgement",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.resetAcknowledgement,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

// ── Quiz thunks ───────────────────────────────────────────────────────────────

/**
 * Creates a new quiz.
 * @param {object} body - Quiz creation payload.
 */
export const createQuiz = createAsyncThunk(
  "vsecurite/createQuiz",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.createQuiz,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Fetches the list of all quizzes.
 * @param {object} body - Filter/pagination payload.
 */
export const fetchQuizzes = createAsyncThunk(
  "vsecurite/fetchQuizzes",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.fetchQuizzes,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Fetches a single quiz by ID.
 * @param {object} body - Payload containing the quiz ID.
 */
export const fetchQuiz = createAsyncThunk(
  "vsecurite/fetchQuiz",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.fetchQuiz,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Updates an existing quiz.
 * @param {object} body - Updated quiz payload.
 */
export const updateQuiz = createAsyncThunk(
  "vsecurite/updateQuiz",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.updateQuiz,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Deletes a quiz by ID.
 * @param {object} body - Payload containing the quiz ID to delete.
 */
export const deleteQuiz = createAsyncThunk(
  "vsecurite/deleteQuiz",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.deleteQuiz,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Publishes a quiz, making it available to collaborators.
 * @param {object} body - Payload containing the quiz ID to publish.
 */
export const publishQuiz = createAsyncThunk(
  "vsecurite/publishQuiz",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.publishQuiz,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Fetches the list of questions for a given quiz (admin view — includes is_correct on options).
 * @param {object} body - Payload containing { id_quiz }.
 */
export const fetchQuizQuestions = createAsyncThunk(
  "vsecurite/fetchQuizQuestions",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.fetchQuizQuestions,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Adds a question to a quiz.
 * @param {object} body - Question payload including quiz ID.
 */
export const addQuestion = createAsyncThunk(
  "vsecurite/addQuestion",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.addQuestion,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Updates an existing question within a quiz.
 * @param {object} body - Updated question payload.
 */
export const updateQuestion = createAsyncThunk(
  "vsecurite/updateQuestion",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.updateQuestion,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Deletes a question from a quiz.
 * @param {object} body - Payload containing the question ID to delete.
 */
export const deleteQuestion = createAsyncThunk(
  "vsecurite/deleteQuestion",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.deleteQuestion,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Imports a batch of questions into a quiz from an external source.
 * @param {object} body - Import payload containing question data.
 */
export const importQuestions = createAsyncThunk(
  "vsecurite/importQuestions",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.importQuestions,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Fetches tracking/completion data for a quiz.
 * @param {object} body - Payload containing the quiz ID.
 */
export const fetchQuizTracking = createAsyncThunk(
  "vsecurite/fetchQuizTracking",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.fetchQuizTracking,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

export const fetchAdminCollabReview = createAsyncThunk(
  "vsecurite/fetchAdminCollabReview",
  async (body) => {
    try {
      return await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.fetchAdminCollabReview,
        body
      );
    } catch (error) {
      return error;
    }
  }
);

/**
 * Exports the tracking data for a quiz.
 * @param {object} body - Payload containing export filters.
 */
export const exportQuizTracking = createAsyncThunk(
  "vsecurite/exportQuizTracking",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.exportQuizTracking,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Fetches the list of quizzes pending completion for a collaborator.
 * @param {object} body - Payload containing the collaborator ID.
 */
export const fetchCollabPendingQuizzes = createAsyncThunk(
  "vsecurite/fetchCollabPendingQuizzes",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.fetchCollabPendingQuizzes,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Fetches the quiz completion history for a collaborator.
 * @param {object} body - Payload containing the collaborator ID.
 */
export const fetchCollabQuizHistory = createAsyncThunk(
  "vsecurite/fetchCollabQuizHistory",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.fetchCollabQuizHistory,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Fetches the review data for a completed quiz attempt.
 * @param {object} body - Payload containing the attempt ID.
 */
export const fetchQuizReview = createAsyncThunk(
  "vsecurite/fetchQuizReview",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.fetchQuizReview,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Fetches all finished attempts for a quiz by the logged-in collab.
 * @param {object} body - Payload containing { id_quiz }.
 */
export const fetchCollabQuizAttempts = createAsyncThunk(
  "vsecurite/fetchCollabQuizAttempts",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.fetchCollabQuizAttempts,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Starts a new quiz attempt for a collaborator and returns the first question.
 * @param {object} body - Payload containing the quiz ID and collaborator ID.
 */
export const startQuizAttempt = createAsyncThunk(
  "vsecurite/startQuizAttempt",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.startQuizAttempt,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Submits an answer for the current question in an active quiz attempt.
 * @param {object} body - Payload containing the attempt ID, question ID, and selected option IDs.
 */
export const submitAnswer = createAsyncThunk(
  "vsecurite/submitAnswer",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.submitAnswer,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Finalizes an active quiz attempt and calculates the results.
 * @param {object} body - Payload containing the attempt ID to finish.
 */
export const finishAttempt = createAsyncThunk(
  "vsecurite/finishAttempt",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.finishAttempt,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Fetches the results for a completed quiz attempt.
 * @param {object} body - Payload containing the attempt ID.
 */
export const fetchAttemptResults = createAsyncThunk(
  "vsecurite/fetchAttemptResults",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.fetchAttemptResults,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

// ── Dashboard thunks ──────────────────────────────────────────────────────────

/**
 * Fetches the overall V-Sécurité dashboard summary for the current scope.
 * @param {object} body - Filter payload (date range, team, etc.).
 */
export const fetchDashboardSummary = createAsyncThunk(
  "vsecurite/fetchDashboardSummary",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.fetchDashboardSummary,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Fetches dashboard statistics broken down by team.
 * @param {object} body - Filter payload.
 */
export const fetchDashboardByTeam = createAsyncThunk(
  "vsecurite/fetchDashboardByTeam",
  async (body) => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.fetchDashboardByTeam,
        body
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

/**
 * Fetches per-collaborator detail rows for the dashboard "Export all" action.
 * Returns { awarenessDetails, quizDetails } — not stored in state, consumed
 * directly by the caller to build an Excel file.
 */
export const fetchDashboardExport = createAsyncThunk(
  "vsecurite/fetchDashboardExport",
  async () => {
    try {
      let response = await NetworkManager.getInstance().postRequest(
        config.apiPaths.vsecurite.fetchDashboardExport,
        {}
      );
      return response;
    } catch (error) {
      return error;
    }
  }
);

// ── Named action exports ──────────────────────────────────────────────────────

export const {
  setAwarenessCampaigns,
  setAwarenessCurrentCampaign,
  setAwarenessTracking,
  setAwarenessCollabPending,
  setAwarenessCollabHistory,
  setAwarenessLoading,
  setAwarenessError,
  setQuizzesList,
  setQuizzesCurrentQuiz,
  setQuizzesTracking,
  setQuizzesCollabPending,
  setQuizzesCollabHistory,
  setQuizzesCurrentAttempt,
  setQuizzesCurrentQuestion,
  setQuizzesAttemptResults,
  setQuizzesReviewData,
  setQuizzesLoading,
  setQuizzesError,
  setDashboardSummary,
  setDashboardByTeam,
  setDashboardLoading,
} = vsecuriteSlice.actions;

export default vsecuriteSlice.reducer;
